import { z } from "zod";
import { bilingualStringSchema } from "./bilingual.ts";

export const genderSchema = z.enum(["male", "female", "unspecified"]);
export type Gender = z.infer<typeof genderSchema>;

export const bloodTypeSchema = z.enum([
	"A+",
	"A-",
	"B+",
	"B-",
	"AB+",
	"AB-",
	"O+",
	"O-",
	"unknown",
]);
export type BloodType = z.infer<typeof bloodTypeSchema>;

export const patientSourceSchema = z.enum([
	"walk_in",
	"whatsapp",
	"instagram",
	"referral",
	"phone",
	"web",
	"staff_manual",
	"import",
	"other",
]);
export type PatientSource = z.infer<typeof patientSourceSchema>;

export const patientChannelSchema = z.enum([
	"whatsapp",
	"sms",
	"call",
	"in_person",
]);
export type PatientChannel = z.infer<typeof patientChannelSchema>;

/**
 * Explicit allergy state so "no known drug allergies" (NKDA) is representable.
 * An empty allergies array alone is dangerously ambiguous: it can mean
 * "nobody asked yet" (unrecorded) or "asked, none known" (nkda).
 */
export const allergyStatusSchema = z.enum([
	"unrecorded",
	"nkda",
	"has_allergies",
]);
export type AllergyStatus = z.infer<typeof allergyStatusSchema>;

/**
 * Pure: the allergy_status a patient should end up with after a create/update.
 * Recording any allergy always wins (has_allergies). Clearing the list falls
 * back to the explicit request (the NKDA button) or to 'unrecorded' — never
 * silently keeps has_allergies with an empty list.
 */
export function nextAllergyStatus(args: {
	current: AllergyStatus;
	/** Allergies array as provided in THIS request (undefined = untouched). */
	allergies?: readonly unknown[] | undefined;
	/** Explicit status requested in this request (e.g. the NKDA button). */
	requested?: AllergyStatus | undefined;
}): AllergyStatus {
	const hasAllergies =
		args.allergies !== undefined
			? args.allergies.length > 0
			: args.current === "has_allergies";
	if (hasAllergies) return "has_allergies";
	if (args.requested && args.requested !== "has_allergies")
		return args.requested;
	// Allergies were explicitly cleared with no explicit status → back to
	// unrecorded (we no longer know; don't claim NKDA on the patient's behalf).
	if (args.allergies !== undefined && args.current === "has_allergies")
		return "unrecorded";
	return args.current;
}

/**
 * Pure: may a prescription be issued given the patient's allergy state?
 * 'unrecorded' blocks unless the prescriber explicitly overrides
 * («متابعة على مسؤوليتي») — nkda / has_allergies are informed states.
 */
export function canIssueWithAllergyStatus(
	status: AllergyStatus,
	override?: boolean,
): boolean {
	return status !== "unrecorded" || override === true;
}

export const allergySchema = z.object({
	substance: z.string().min(1),
	severity: z.enum(["mild", "moderate", "severe"]),
	reaction: z.string().optional(),
	noted_at: z.string().datetime().optional(),
});
export type Allergy = z.infer<typeof allergySchema>;

export const chronicConditionSchema = z.object({
	name: bilingualStringSchema,
	diagnosed_at: z.string().date().optional(),
	notes: z.string().optional(),
});
export type ChronicCondition = z.infer<typeof chronicConditionSchema>;

/**
 * A medication the patient is currently taking from any source. Separate
 * from prescriptions issued by this clinic so the doctor can record
 * "patient is on metformin from another doctor" without forcing them
 * to mint a clinic-side prescription.
 */
export const activeMedicationSchema = z.object({
	name: z.string().min(1).max(200),
	dosage: z.string().max(100).optional(),
	frequency: z.string().max(100).optional(),
	since_date: z.string().date().optional(),
	notes: z.string().max(500).optional(),
});
export type ActiveMedication = z.infer<typeof activeMedicationSchema>;

export const patientSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	mrn: z.string(),
	full_name: z.string().min(1),
	phone: z.string(),
	email: z.string().email().nullable(),
	date_of_birth: z.string().date().nullable(),
	gender: genderSchema,
	national_id: z.string().nullable(),
	address: z.string().nullable(),
	governorate: z.string().nullable(),
	area: z.string().nullable(),
	preferred_language: z.enum(["ar", "en"]),
	preferred_channel: patientChannelSchema,
	emergency_contact_name: z.string().nullable(),
	emergency_contact_phone: z.string().nullable(),
	blood_type: bloodTypeSchema,
	/** Fitzpatrick skin phototype I–VI (1–6); null = not assessed. */
	fitzpatrick: z.number().int().min(1).max(6).nullable(),
	allergy_status: allergyStatusSchema,
	allergies: z.array(allergySchema),
	chronic_conditions: z.array(chronicConditionSchema),
	active_medications: z.array(activeMedicationSchema),
	notes: z.string().nullable(),
	source: patientSourceSchema,
	tags: z.array(z.string()),
	is_active: z.boolean(),
	deleted_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type Patient = z.infer<typeof patientSchema>;

// ===== Input schemas =====

export const createPatientInputSchema = z.object({
	full_name: z.string().min(2).max(120),
	phone: z.string().min(8).max(20),
	email: z.string().email().optional().or(z.literal("")),
	date_of_birth: z.string().date().optional().or(z.literal("")),
	gender: genderSchema.default("unspecified"),
	national_id: z.string().optional().or(z.literal("")),
	address: z.string().optional().or(z.literal("")),
	governorate: z.string().max(80).optional().or(z.literal("")),
	area: z.string().max(120).optional().or(z.literal("")),
	preferred_language: z.enum(["ar", "en"]).default("ar"),
	preferred_channel: patientChannelSchema.default("whatsapp"),
	emergency_contact_name: z.string().optional().or(z.literal("")),
	emergency_contact_phone: z.string().optional().or(z.literal("")),
	blood_type: bloodTypeSchema.default("unknown"),
	fitzpatrick: z.number().int().min(1).max(6).nullable().optional(),
	/** Explicit NKDA / unrecorded marker; recording allergies forces has_allergies. */
	allergy_status: allergyStatusSchema.optional(),
	allergies: z.array(allergySchema).default([]),
	chronic_conditions: z.array(chronicConditionSchema).default([]),
	active_medications: z.array(activeMedicationSchema).default([]),
	notes: z.string().optional().or(z.literal("")),
	source: patientSourceSchema.default("staff_manual"),
	tags: z.array(z.string()).default([]),
	/** Set true to bypass the same-name + same-DOB duplicate guard (the operator
	 *  confirmed it's genuinely a different person who shares both). */
	allow_duplicate: z.boolean().optional(),
});
export type CreatePatientInput = z.infer<typeof createPatientInputSchema>;

export const updatePatientInputSchema = createPatientInputSchema.partial();
export type UpdatePatientInput = z.infer<typeof updatePatientInputSchema>;

// ===== Bulk import (CSV migration from another system) =====

// One raw row, deliberately lenient: only name + phone matter. Everything
// else is best-effort and normalised server-side. The client parses the CSV
// and maps columns to these fields before sending.
export const importPatientRowSchema = z.object({
	full_name: z.string().trim().max(120).default(""),
	phone: z.string().trim().max(40).default(""),
	email: z.string().trim().max(160).optional().default(""),
	date_of_birth: z.string().trim().max(40).optional().default(""),
	gender: z.string().trim().max(20).optional().default(""),
	national_id: z.string().trim().max(40).optional().default(""),
	address: z.string().trim().max(400).optional().default(""),
	notes: z.string().trim().max(2000).optional().default(""),
});
export type ImportPatientRow = z.infer<typeof importPatientRowSchema>;

export const importPatientsInputSchema = z.object({
	rows: z.array(importPatientRowSchema).min(1).max(2000),
});
export type ImportPatientsInput = z.infer<typeof importPatientsInputSchema>;

export const importSkippedRowSchema = z.object({
	row: z.number().int(), // 1-based index within the submitted batch
	reason: z.enum([
		"missing_name",
		"invalid_phone",
		"duplicate_existing",
		"duplicate_in_file",
	]),
	name: z.string().optional(),
});
export type ImportSkippedRow = z.infer<typeof importSkippedRowSchema>;

export const importPatientsResultSchema = z.object({
	total: z.number().int(),
	imported: z.number().int(),
	skipped: z.array(importSkippedRowSchema),
});
export type ImportPatientsResult = z.infer<typeof importPatientsResultSchema>;

export const patientsListQuerySchema = z.object({
	q: z.string().optional(),
	is_active: z.coerce.boolean().optional(),
	page: z.coerce.number().int().positive().default(1),
	per_page: z.coerce.number().int().positive().max(100).default(20),
	sort: z.enum(["created_at", "full_name", "updated_at"]).default("created_at"),
	order: z.enum(["asc", "desc"]).default("desc"),
});
export type PatientsListQuery = z.infer<typeof patientsListQuerySchema>;
